import React, { useMemo } from "react";
import { EntityBillingCountResponse } from "@common/schemas/billing.schema";
import { Entity } from "@common/types/entity";
import { currencyFormatterFractionsIfNeeded } from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { View, ViewDensity } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import styles from "./AdditionalEntitiesModal.module.scss";

interface Props {
    show: boolean;
    onHide: () => void;
    entityBillingCount: EntityBillingCountResponse;
    businessEntities: Entity[];
}

interface AdditionalEntityRow {
    id: number;
    entity: Entity | null;
    price: string;
}

const additionalEntitiesView: View = {
    columns: [{ id: "entity" }, { id: "price", options: { size: 180 } }],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.SM,
    },
};

const additionalEntitiesConfig: ViewConfig<
    AdditionalEntityRow,
    "entity" | "price",
    never
> = {
    columns: {
        entity: {
            name: "Entity",
            header: "Entity",
            accessorKey: "entity",
            minSize: 240,
            maxSize: 600,
            cell: ({
                row,
            }: CellContext<
                ViewTableFeatures,
                AdditionalEntityRow,
                Entity | null
            >) => (
                <ViewTableCell className={styles.entity}>
                    {row.original.entity && (
                        <EntityAvatar
                            entity={row.original.entity}
                            size="xs"
                            aria-hidden
                        />
                    )}
                    <span className={styles.entityName}>
                        {row.original.entity?.name ?? "Unknown entity"}
                    </span>
                </ViewTableCell>
            ),
        },
        price: {
            name: "Additional price/month",
            header: "Additional price/month",
            accessorKey: "price",
            minSize: 160,
            maxSize: 220,
            cell: ({
                row,
            }: CellContext<ViewTableFeatures, AdditionalEntityRow, string>) => (
                <ViewTableCell className={styles.price}>
                    {row.original.price}
                </ViewTableCell>
            ),
        },
    },
};

function getAdditionalEntityPrice({
    isExcludedFromBilling,
    isIncludedInLicense,
    isPaid,
    paidPrice,
}: {
    isExcludedFromBilling: boolean;
    isIncludedInLicense: boolean;
    isPaid: boolean;
    paidPrice: string;
}) {
    if (isPaid) {
        return paidPrice;
    }
    if (isIncludedInLicense) {
        return "Covered by license";
    }
    if (isExcludedFromBilling) {
        return "Excluded from billing";
    }
    return "$0";
}

export const AdditionalEntitiesModal: React.FC<Props> = ({
    show,
    onHide,
    entityBillingCount,
    businessEntities,
}) => {
    const businessEntitiesById = useMemo(
        () => new Map(businessEntities.map((entity) => [entity.id, entity])),
        [businessEntities],
    );
    const paidPrice = currencyFormatterFractionsIfNeeded.format(
        fromIntToMonetary(
            entityBillingCount.additionalEntityMonthlyAmountCents,
        ),
    );
    const rows = useMemo(
        () =>
            entityBillingCount.entities.map(
                ({
                    entityId,
                    isExcludedFromBilling,
                    isIncludedInLicense,
                    isPaid,
                }) => ({
                    id: entityId,
                    entity: businessEntitiesById.get(entityId) ?? null,
                    price: getAdditionalEntityPrice({
                        isExcludedFromBilling,
                        isIncludedInLicense,
                        isPaid,
                        paidPrice,
                    }),
                }),
            ),
        [businessEntitiesById, entityBillingCount.entities, paidPrice],
    );

    return (
        <StandardModal show={show} onHide={onHide} size="lg">
            <StandardModalHeader>
                <h4 className="mb-0">Additional entities</h4>
            </StandardModalHeader>
            <StandardModalBody>
                <ViewTable
                    data={rows}
                    view={additionalEntitiesView}
                    config={additionalEntitiesConfig}
                    className={styles.table}
                    rowBorders
                    outerBorder
                    initialVisibleRows={rows.length}
                    enableKeyboardShortcuts={false}
                />
            </StandardModalBody>
        </StandardModal>
    );
};
